/**
 * Converts a UTC date/time string to a user's local timezone string for display.
 * @param {string} utcDateString - The appointment date and time in UTC (e.g., '2025-11-20T14:30:00Z').
 * @param {string} localTimezone - The user's local timezone string (e.g., 'America/Los_Angeles').
 * @returns {{time: string, timezoneShort: string, timezoneLong: string}} */
const convertUTCToLocalDisplayTime = (utcDateString, localTimezone) => {
  if (!utcDateString || !localTimezone)
    return { time: "N/A", timezoneShort: "", timezoneLong: "" };

  try {
    const date = new Date(utcDateString);

    const timeFormatter = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: localTimezone,
      hour12: true,
    });
    const timePart = timeFormatter.format(date);

    const tzShortFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: localTimezone,
      timeZoneName: "shortOffset",
    });
    const tzShortPart = tzShortFormatter.format(date).split(" ").pop();

    const tzLongFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: localTimezone,
      timeZoneName: "long",
    });
    const parts = tzLongFormatter.formatToParts(date);
    const tzLongPart =
      parts.find((part) => part.type === "timeZoneName")?.value ||
      localTimezone;

    return {
      time: timePart,
      timezoneShort: tzShortPart,
      timezoneLong: tzLongPart,
    };
  } catch (e) {
    console.error("Date conversion error:", e);
    return { time: "Invalid Time", timezoneShort: "N/A", timezoneLong: "N/A" };
  }
};

const convertUTCToLocalDisplayDate = (utcDateString, localTimezone) => {
  if (!utcDateString || !localTimezone) return "N/A";

  try {
    const date = new Date(utcDateString);

    const dateOptions = {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: localTimezone,
    };

    const formatter = new Intl.DateTimeFormat("en-US", dateOptions);
    return formatter.format(date);
  } catch (e) {
    console.error("Date conversion error:", e);
    return "Invalid Date";
  }
};

const calculateAge = (birthDate) => {
  if (!birthDate) return "N/A";

  const birth = new Date(birthDate);
  if (isNaN(birth.getTime())) return "N/A"; // safely handle invalid date strings

  const today = new Date();
  const diffInMs = today.getTime() - birth.getTime(); // use getTime() for numeric difference

  const ageDate = new Date(diffInMs); // convert ms diff back to Date object
  const age = Math.abs(ageDate.getUTCFullYear() - 1970);

  return `${age} yrs`;
};

export {
    convertUTCToLocalDisplayDate, convertUTCToLocalDisplayTime, calculateAge
}